import { type InstrumentId, TIMEFRAMES, type Timeframe } from "@trader/shared/MarketData"
import type { AccountSummary } from "@trader/shared/Money"
import { BigDecimal, Option } from "effect"
import { useEffect, useRef, useState } from "react"
import { Modal } from "../../ui/Modal.tsx"
import { formatMoney } from "./format.ts"
import {
  ACCOUNT_CURRENCIES,
  buildCreateAccount,
  type ChargeChoice,
  defaultForm,
  defaultReplayFrom,
  intradayNote,
  type NewAccountForm,
  withCurrency
} from "./newAccount.ts"
import { messageFor, tradingApi } from "./tradingApi.ts"
import { accountsStore } from "./tradingData.ts"

const CHARGE_OPTIONS: ReadonlyArray<readonly [ChargeChoice, string]> = [
  ["FyersNseEquity", "Fyers NSE equity"],
  ["Zero", "None"],
  ["Percent", "Percent of value"],
  ["PerOrder", "Flat per fill"]
]

/** A live preview of the starting cash, formatted in the account currency. */
const cashPreview = (text: string, currency: string): string => {
  const parsed = BigDecimal.fromString(text.replace(/[,\s_]/g, ""))
  return Option.isSome(parsed) && text.trim() !== "" ? formatMoney(parsed.value, currency) : ""
}

/** Creates a paper account: currency, cash, clock (realtime or replay), charges and fills. */
export function NewAccountDialog({ defaultCurrency, defaultInstrument, defaultTimeframe, onClose, onCreated }: {
  readonly defaultCurrency: string
  /** The active chart's symbol: the default replay start is inside its stored bars. */
  readonly defaultInstrument: InstrumentId | null
  readonly defaultTimeframe: Timeframe
  readonly onClose: () => void
  readonly onCreated: (account: AccountSummary) => void
}) {
  const [form, setForm] = useState<NewAccountForm>(() =>
    defaultForm(ACCOUNT_CURRENCIES.includes(defaultCurrency) ? defaultCurrency : "INR", defaultTimeframe, new Date())
  )
  const [attempted, setAttempted] = useState(false)
  const [busy, setBusy] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const check = buildCreateAccount(form, Math.floor(Date.now() / 1000))
  const errors = attempted && !check.ok ? check.errors : {}
  const change = (patch: Partial<NewAccountForm>) => {
    setForm((current) => ({ ...current, ...patch }))
    setServerError(null)
  }
  const note = intradayNote(form)

  // The default replay start follows the stored bars of the chart's symbol at the
  // chosen timeframe, until the user picks a date of their own.
  const fromEdited = useRef(false)
  useEffect(() => {
    if (defaultInstrument === null) return
    let current = true
    tradingApi.latestStoredBar(defaultInstrument, form.timeframe).then(
      (latest) => {
        if (current && !fromEdited.current) setForm((f) => ({ ...f, from: defaultReplayFrom(f.timeframe, latest, new Date()) }))
      },
      () => {
        // Keep the fallback date.
      }
    )
    return () => {
      current = false
    }
  }, [defaultInstrument, form.timeframe])

  const submit = () => {
    setAttempted(true)
    if (!check.ok || busy) return
    setBusy(true)
    accountsStore.create(check.request).then(
      (account) => {
        setBusy(false)
        onCreated(account)
      },
      (error: unknown) => {
        setBusy(false)
        const message = messageFor(error)
        setServerError(`${message.title}: ${message.detail}`)
      }
    )
  }

  return (
    <Modal title="New paper account" onClose={onClose}>
      <form
        noValidate
        className="settings-form account-form"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <label>
          Name
          <input type="text" value={form.name} maxLength={64} onChange={(event) => change({ name: event.target.value })} />
        </label>
        {errors.name && <p className="field-error">{errors.name}</p>}
        <label>
          Currency
          <select value={form.currency} onChange={(event) => setForm((current) => withCurrency(current, event.target.value))}>
            {ACCOUNT_CURRENCIES.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
          </select>
        </label>
        <p className="field-note muted">
          Balances are in {form.currency}. Instruments priced in another currency are converted: their P&amp;L, charges and margin
          at the previous day's ECB rate.
        </p>
        <label>
          Starting cash
          <input
            type="text"
            inputMode="decimal"
            value={form.startingCash}
            onChange={(event) => change({ startingCash: event.target.value })}
          />
        </label>
        {errors.startingCash
          ? <p className="field-error">{errors.startingCash}</p>
          : <p className="field-note muted">{cashPreview(form.startingCash, form.currency)}</p>}

        <fieldset>
          <legend>Clock</legend>
          <div className="segmented" role="radiogroup" aria-label="Clock">
            {(["Replay", "Realtime"] as const).map((clock) => (
              <button key={clock} type="button" role="radio" aria-checked={form.clock === clock} onClick={() => change({ clock })}>
                {clock === "Replay" ? "Replay (bar by bar)" : "Realtime"}
              </button>
            ))}
          </div>
          <p className="field-note muted">
            {form.clock === "Replay"
              ? "Replays stored bars from a start date: play, pause and step through history while you trade."
              : "Advances as new bars are stored (nightly today; live once the broker feed is connected)."}
          </p>
          <label>
            Timeframe
            <select value={form.timeframe} onChange={(event) => {
              const timeframe = TIMEFRAMES.find((tf) => tf === event.target.value)
              if (timeframe) change({ timeframe })
            }}>
              {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </label>
          {form.clock === "Replay" && (
            <label>
              Start date
              <input
                type="date"
                value={form.from}
                onChange={(event) => {
                  fromEdited.current = true
                  change({ from: event.target.value })
                }}
              />
            </label>
          )}
          {errors.from && <p className="field-error">{errors.from}</p>}
          {note && <p className="field-note muted">{note}</p>}
        </fieldset>

        <fieldset>
          <legend>Charges and fills</legend>
          <label>
            Charges
            <select
              value={form.charges}
              onChange={(event) => {
                const charges = CHARGE_OPTIONS.find(([value]) => value === event.target.value)?.[0]
                if (charges) change({ charges })
              }}
            >
              {CHARGE_OPTIONS.filter(([value]) => value !== "FyersNseEquity" || form.currency === "INR").map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </label>
          {form.charges === "Percent" && (
            <>
              <label>
                Basis points of value
                <input type="text" inputMode="decimal" value={form.bps} onChange={(event) => change({ bps: event.target.value })} />
              </label>
              <label>
                Minimum per fill ({form.currency})
                <input type="text" inputMode="decimal" value={form.minFee} onChange={(event) => change({ minFee: event.target.value })} />
              </label>
            </>
          )}
          {form.charges === "PerOrder" && (
            <label>
              Per fill ({form.currency})
              <input type="text" inputMode="decimal" value={form.perFill} onChange={(event) => change({ perFill: event.target.value })} />
            </label>
          )}
          {errors.charges
            ? <p className="field-error">{errors.charges}</p>
            : form.charges === "FyersNseEquity" && (
              <p className="field-note muted">Brokerage, STT, exchange charges, SEBI fees, stamp duty and GST, by product (CNC, MIS, NRML).</p>
            )}
          <label>
            Limit orders fill
            <select
              value={form.fillModel}
              onChange={(event) => change({ fillModel: event.target.value === "conservative" ? "conservative" : "engine-parity" })}
            >
              <option value="engine-parity">On touch (as the backtester)</option>
              <option value="conservative">Only when traded through</option>
            </select>
          </label>
          {form.currency === "INR" && (
            <label>
              Intraday square-off (IST)
              <input type="text" value={form.squareOffTime} onChange={(event) => change({ squareOffTime: event.target.value })} />
            </label>
          )}
          {errors.squareOffTime && <p className="field-error">{errors.squareOffTime}</p>}
        </fieldset>

        {serverError && <p className="form-error">{serverError}</p>}
        <footer className="settings-actions">
          <span className="toolbar-spacer" />
          <button type="button" onClick={onClose}>Cancel</button>
          <button type="submit" className="primary" disabled={busy}>{busy ? "Creating…" : "Create account"}</button>
        </footer>
      </form>
    </Modal>
  )
}
